/**
 * <TutorReleaseSlotsPage> — « Libérer des créneaux » : le masteur rend des créneaux qui portent ses
 * lives (page à étapes).
 *
 * Legacy `ReleaseSlotsScreen` + `ReleaseSlotsSteps` (route `tutor/liberer-creneaux`), en deux étapes
 * sous UN en-tête (`<Header steps>`), qui entrent l'une après l'autre (`<StepStage>`) :
 *  1. **Ta semaine** — la même grille que « Ma dispo » (`<TutorWeekGrid>`) : toucher une heure qui
 *     porte un live la barre (un second toucher la rend), toucher une heure libre la propose ou la
 *     retire — les familles verront cette semaine. Ouverte depuis un live, la première ligne dit
 *     lequel est déjà libéré. Deux actions : « Suivant » (dès qu'une heure est marquée) et « J'arrête
 *     tous mes créneaux » ; « une absence ponctuelle n'est pas ce geste » se lit dessous.
 *  2. **Tes élèves** — l'aperçu du serveur, une carte par élève, la plus grave en tête
 *     (`<TutorReleaseStudentCard>`), puis les lives qui ne sont pas concernés. Un refus du serveur se
 *     lit en tête, et rien ne se confirme. « Confirmer » est la seule action.
 *  Entre les deux, quand l'aperçu peut coûter des élèves, la boîte « Tu risques de perdre… »
 *  (`dialog`).
 *  - **C'est fait** — « C'est noté, merci Camille », et le retour : à « Ma dispo », ou à ses lives
 *    quand le parcours est parti d'un live.
 *
 * Contrôlée : l'étape, la semaine, l'aperçu, les envois en cours et la boîte arrivent en props. Rien
 * ne s'écrit avant « Confirmer » : c'est l'écran qui le garantit.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { StepStage } from "../../atoms/StepStage"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { Header } from "../../molecules/Header"
import { TutorReleaseStudentCard } from "../../organisms/TutorReleaseStudentCard"
import { TutorWeekGrid } from "../../organisms/TutorWeekGrid"
import { PageTemplate } from "../../templates/PageTemplate"
import { TutorAvailabilityDialog, type TutorAvailabilityDialogProps } from "../../templates/TutorAvailabilityDialog"
import { TUTOR_FORM_WIDTH } from "../TutorAvailabilityPage/TutorAvailabilityPage"
import { TutorReleaseSlotsSkeleton } from "./TutorReleaseSlotsSkeleton"
import {
    type UITutorHour,
    type UITutorReleasePreview,
    type UITutorReleaseStep,
    type UITutorReleaseSuggestedSlot,
    type UITutorWeekDay,
} from "../../../ports/primary/ui/types/tutorDispoOffres"

const TEXTS = {
    name: "Libérer des créneaux",
    weekTitle: "Ta semaine",
    studentsTitle: "Tes élèves",
    preselected: (label: string) => `Ton ${label} est libéré. D'autres créneaux changent ?`,
    hintPreselected:
        "Touche un autre créneau avec un live pour le libérer aussi. Touche une heure libre pour la proposer ou la retirer : les familles verront cette semaine.",
    hint: "Touche les créneaux avec un live que tu ne peux plus assurer. Touche une heure libre pour la proposer ou la retirer : les familles verront cette semaine.",
    stopAll: "J'arrête tous mes créneaux",
    absence: "Une absence ponctuelle n'est pas ce geste : fais une demande d'absence.",
    next: "Suivant",
    confirm: "Confirmer",
    nobody: "Aucun élève n'est concerné.",
    done: (firstname?: string) => `C'est noté, merci ${firstname ?? ""}`.trim(),
    doneDetail: "Tes créneaux sont libérés. Les familles sont prévenues.",
    backToDispo: "Revenir à Ma dispo",
    backToLives: "Revenir à mes lives",
}

export type TutorReleaseSlotsPageProps = {
    step: UITutorReleaseStep
    /** La semaine du brouillon : les heures marquées sont `freed` + `releasable`. `null` tant qu'elle charge. */
    days: UITutorWeekDay[] | null
    /** Ouvert depuis un live : « lundi 18h30 », « essai du mardi à 17h ». */
    preselectedLabel?: string
    onPressHour: (hour: UITutorHour) => void
    /** Une heure est marquée (ou le parcours vient d'un essai, d'un stage) : « Suivant » se montre. */
    canContinue: boolean
    onContinue: () => void
    /** L'aperçu demandé par « Suivant » est en route. */
    continuing?: boolean
    onStopAll: () => void
    /** L'aperçu demandé par « J'arrête tous mes créneaux » est en route. */
    stoppingAll?: boolean

    preview: UITutorReleasePreview | null
    onOpenSlot: (slot: UITutorReleaseSuggestedSlot) => void
    /** L'aperçu repart après un créneau ouvert : « Confirmer » attend. */
    opening?: boolean
    onConfirm: () => void
    confirming?: boolean

    /** « C'est noté, merci… » */
    tutorFirstname?: string
    /** Le parcours est parti d'un live : le retour mène à ses lives. */
    fromLive?: boolean
    onDone: () => void

    /** Retour de « Tes élèves » à « Ta semaine ». */
    onBack: () => void
    onClose: () => void
    /** La boîte « Tu risques de perdre… », s'il y a lieu. */
    dialog?: TutorAvailabilityDialogProps | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorReleaseSlotsPage(props: TutorReleaseSlotsPageProps) {
    const {
        step,
        days,
        preselectedLabel,
        onPressHour,
        canContinue,
        onContinue,
        continuing = false,
        onStopAll,
        stoppingAll = false,
        preview,
        onOpenSlot,
        opening = false,
        onConfirm,
        confirming = false,
        tutorFirstname,
        fromLive = false,
        onDone,
        onBack,
        onClose,
        dialog,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    if (step === "done") {
        return (
            <PageTemplate testID={testID} padding="4" maxContentWidth={TUTOR_FORM_WIDTH}>
                <StepStage stepKey="done" enter="entree">
                    <FlowOutcome
                        center
                        iconName="check-circle"
                        tone="success"
                        message={TEXTS.done(tutorFirstname)}
                        detail={TEXTS.doneDetail}
                    >
                        <Button label={fromLive ? TEXTS.backToLives : TEXTS.backToDispo} onPress={onDone} />
                    </FlowOutcome>
                </StepStage>
            </PageTemplate>
        )
    }

    const onWeek = step === "week"
    const header = (
        <Header
            title={onWeek ? TEXTS.weekTitle : TEXTS.studentsTitle}
            steps={{ step: onWeek ? 1 : 2, total: 2, onBack: onWeek ? undefined : onBack, onClose }}
        />
    )

    const footer = onWeek ? (
        canContinue ? (
            <Button label={TEXTS.next} onPress={onContinue} loading={continuing} disabled={stoppingAll} fullWidth />
        ) : undefined
    ) : preview != null && preview.refusal == null && !opening ? (
        <Button label={TEXTS.confirm} onPress={onConfirm} loading={confirming} fullWidth />
    ) : undefined

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorReleaseSlotsSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={TUTOR_FORM_WIDTH}
            footer={footer}
        >
            <StepStage stepKey={step}>
                {onWeek ? (
                    <Stack gap="5">
                        <Stack gap="2">
                            {preselectedLabel != null && preselectedLabel !== "" && (
                                <Text size="text-lg" weight="bold">
                                    {TEXTS.preselected(preselectedLabel)}
                                </Text>
                            )}
                            <Text size="text-md" color="text-muted">
                                {preselectedLabel != null && preselectedLabel !== ""
                                    ? TEXTS.hintPreselected
                                    : TEXTS.hint}
                            </Text>
                        </Stack>
                        {days != null && <TutorWeekGrid days={days} onPressHour={onPressHour} />}
                        <Stack gap="2" align="start">
                            <TextButton label={TEXTS.stopAll} onPress={onStopAll} loading={stoppingAll} />
                            <Text size="text-md" color="text-muted">
                                {TEXTS.absence}
                            </Text>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack gap="3">
                        {preview?.refusal != null && preview.refusal !== "" && (
                            <AlertCard severity="warning" message={preview.refusal} />
                        )}
                        {preview?.cards.map((card) => (
                            <TutorReleaseStudentCard
                                key={card.id}
                                card={card}
                                onOpenSlot={onOpenSlot}
                                opening={opening}
                            />
                        ))}
                        {preview != null && preview.refusal == null && preview.cards.length === 0 && (
                            <Text size="text-md" color="text-muted">
                                {TEXTS.nobody}
                            </Text>
                        )}
                        {preview?.untouchedLines.map((line) => (
                            <Text key={line} size="text-md" color="text-muted">
                                {line}
                            </Text>
                        ))}
                    </Stack>
                )}
            </StepStage>
            {dialog != null && <TutorAvailabilityDialog {...dialog} />}
        </PageTemplate>
    )
}
